'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { toggleBookmark, getBookmarkedIds, trackResonance, BookmarkInput } from '@/lib/bookmarks';

interface BookmarkButtonProps {
  item: BookmarkInput;
  size?: 'sm' | 'md';
  showLabel?: boolean;
  className?: string;
}

export default function BookmarkButton({ item, size = 'sm', showLabel = false, className = '' }: BookmarkButtonProps) {
  const [bookmarked, setBookmarked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getBookmarkedIds(item.item_type).then(ids => {
      if (!cancelled) {
        setBookmarked(ids.has(item.item_id));
        setChecked(true);
      }
    });
    return () => { cancelled = true; };
  }, [item.item_id, item.item_type]);

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    const result = await toggleBookmark(item);
    if (result.error) {
      setToast(result.error);
      setTimeout(() => setToast(null), 2500);
    } else {
      setBookmarked(result.bookmarked);
      setToast(result.bookmarked ? 'Saved to collection' : 'Removed from collection');
      setTimeout(() => setToast(null), 1800);
      if (result.bookmarked) {
        trackResonance(item.item_type, item.item_id, item.item_title, 'bookmark');
      }
    }
    setLoading(false);
  }, [item, loading]);

  if (!checked) return null;

  const iconSize = size === 'sm' ? 14 : 18;

  return (
    <div className={`relative inline-flex items-center gap-1 ${className}`}>
      <button
        onClick={handleClick}
        title={bookmarked ? 'Remove bookmark' : 'Save to collection'}
        style={{
          background: bookmarked ? 'rgba(201,168,76,0.12)' : 'transparent',
          border: `1px solid ${bookmarked ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.2)'}`,
          color: bookmarked ? '#c9a84c' : 'rgba(201,168,76,0.4)',
          borderRadius: 4,
          padding: size === 'sm' ? '3px 6px' : '5px 10px',
          cursor: loading ? 'wait' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          transition: 'all 0.2s ease',
          fontSize: size === 'sm' ? 11 : 13,
        }}
        onMouseEnter={e => {
          if (!bookmarked) (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(201,168,76,0.5)';
        }}
        onMouseLeave={e => {
          if (!bookmarked) (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(201,168,76,0.2)';
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill={bookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
        {showLabel && (
          <span className="font-mono uppercase tracking-widest" style={{ fontSize: size === 'sm' ? 9 : 11 }}>
            {bookmarked ? 'Saved' : 'Save'}
          </span>
        )}
      </button>
      {toast && (
        <div
          className="absolute z-50 font-mono text-[10px] tracking-widest uppercase whitespace-nowrap px-2 py-1 rounded"
          style={{
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: 6,
            background: '#0a1a12',
            border: '1px solid rgba(201,168,76,0.4)',
            color: '#c9a84c',
            pointerEvents: 'none',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
